import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { HomeAnnouncementItem, HomeAnnouncementUpsertParams } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'invalid home announcement': '公告内容不能为空',
      'home announcement not found': '该公告不存在或已被删除',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useHomeAnnouncements() {
  const addToast = useToastStore((s) => s.addToast);
  const [items, setItems] = useState<HomeAnnouncementItem[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getHomeAnnouncements();
      setItems((result.items ?? []).map(normalizeAnnouncementItem));
      setUpdatedAt(result.updated_at);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(async (params: HomeAnnouncementUpsertParams) => {
    setSaving(true);
    try {
      const result = await settingsService.createHomeAnnouncement(params);
      setItems((result.items ?? []).map(normalizeAnnouncementItem));
      setUpdatedAt(result.updated_at);
      addToast('success', '公告已添加');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  const update = useCallback(async (id: string, params: HomeAnnouncementUpsertParams) => {
    setSaving(true);
    try {
      const result = await settingsService.updateHomeAnnouncement(id, params);
      setItems((result.items ?? []).map(normalizeAnnouncementItem));
      setUpdatedAt(result.updated_at);
      addToast('success', '公告已更新');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  const remove = useCallback(async (id: string) => {
    setSaving(true);
    try {
      const result = await settingsService.deleteHomeAnnouncement(id);
      setItems((result.items ?? []).map(normalizeAnnouncementItem));
      setUpdatedAt(result.updated_at);
      addToast('success', '公告已删除');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  return { items, updatedAt, loading, saving, error, reload: load, create, update, remove };
}

function normalizeAnnouncementItem(item: HomeAnnouncementItem): HomeAnnouncementItem {
  return {
    ...item,
    market_id: item.market_id?.trim() || 'CN',
  };
}
